import { getDirectusToken } from '../../../utils/localStorage';
import { loginDirectus } from './login';

const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL;
const UTILITY_BILL_COLLECTION = process.env.REACT_APP_DIRECTUS_UTILITY_BILL_COLLECTION || 'utility_bill';
const UTILITY_BILL_FILES_COLLECTION = process.env.REACT_APP_DIRECTUS_UTILITY_BILL_FILES_COLLECTION || 'utility_bill_files';

async function fetchWithAuth(url, options = {}, { user, password } = {}) {
  let token = getDirectusToken();
  if (!token && user && password) {
    token = await loginDirectus(user, password);
  }
  let response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: options.body ? JSON.stringify(options.body) : undefined
  });
  if (response.status === 401 && user && password) {
    token = await loginDirectus(user, password);
    response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: options.body ? JSON.stringify(options.body) : undefined
    });
  }
  return response;
}

/**
 * Erstellt einen utility_bill (ohne Dateien).
 * Dateien werden über utility_bill_files (utility_bill_id, directus_files_id) verknüpft.
 */
export async function createUtilityBill(
  {
    chargepoint,
    user_electricity_tariff,
    org_unit,
    status = 'entwurf'
  },
  { user, password } = {}
) {
  if (!DIRECTUS_URL) throw new Error('REACT_APP_DIRECTUS_URL fehlt.');
  const token = getDirectusToken() || (user && password ? await loginDirectus(user, password) : null);
  if (!token) throw new Error('Nicht authentifiziert (Directus).');

  const body = {
    status,
    chargepoint: chargepoint ?? null,
    user_electrictiy_tariff: user_electricity_tariff ?? null,
    org_unit: org_unit ?? null
  };

  const url = `${DIRECTUS_URL.replace(/\/$/, '')}/items/${UTILITY_BILL_COLLECTION}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body)
  });

  const text = await response.text().catch(() => '');
  if (!response.ok) {
    throw new Error(`Directus utility_bill erstellen: ${response.status} ${text}`);
  }
  if (!text || text.trim() === '') {
    if (response.status === 204) {
      const id = getIdFrom204Response(response, url, org_unit, token);
      if (id != null) return { id };
      throw new Error('Directus utility_bill: 204 ohne ID (Location-Header prüfen oder GET nachlegen).');
    }
    throw new Error('Directus utility_bill: leere Antwort (kein JSON). Status: ' + response.status);
  }
  let payload;
  try {
    payload = JSON.parse(text);
  } catch (e) {
    throw new Error('Directus utility_bill: ungültiges JSON. ' + text.slice(0, 200));
  }
  const data = payload?.data ?? payload;
  const id = data?.id;
  if (id == null) throw new Error('Directus utility_bill: keine id in Antwort.');
  return data;
}

function getIdFrom204Response(response, postUrl, orgUnit, token) {
  const location = response.headers.get('Location');
  if (location) {
    const parts = location.split('/');
    const id = parts[parts.length - 1];
    if (id && /^\d+$/.test(id)) return parseInt(id, 10);
    if (id && /^[a-f0-9-]+$/i.test(id)) return id;
  }
  return fetchLatestUtilityBillId(postUrl, orgUnit, token);
}

async function fetchLatestUtilityBillId(_postUrl, orgUnit, token) {
  const params = new URLSearchParams({ sort: '-id', limit: '1', fields: 'id' });
  if (orgUnit != null && orgUnit !== '') {
    params.set('filter[org_unit][_eq]', orgUnit);
  }
  const base = DIRECTUS_URL.replace(/\/$/, '');
  const url = `${base}/items/${UTILITY_BILL_COLLECTION}?${params.toString()}`;
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) return null;
  const body = await res.text();
  if (!body) return null;
  try {
    const data = JSON.parse(body);
    const list = data?.data ?? data;
    const first = Array.isArray(list) ? list[0] : list;
    return first?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Legt einen Eintrag in utility_bill_files an.
 * Attribute: utility_bill_id, directus_files_id
 */
export async function createUtilityBillFile(utilityBillId, directusFilesId, { user, password } = {}) {
  if (!DIRECTUS_URL) throw new Error('REACT_APP_DIRECTUS_URL fehlt.');
  const token = getDirectusToken() || (user && password ? await loginDirectus(user, password) : null);
  if (!token) throw new Error('Nicht authentifiziert (Directus).');

  const url = `${DIRECTUS_URL.replace(/\/$/, '')}/items/${UTILITY_BILL_FILES_COLLECTION}`;
  const body = { utility_bill_id: utilityBillId, directus_files_id: directusFilesId };
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body)
  });
  const text = await response.text().catch(() => '');
  if (!response.ok) {
    throw new Error(`Directus utility_bill_files: ${response.status} ${text}`);
  }
  if (!text || text.trim() === '') {
    return { ok: true };
  }
  try {
    const payload = JSON.parse(text);
    return payload?.data ?? payload;
  } catch {
    return { ok: true };
  }
}
